import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { serverMessage } from '@/Ship/Helpers/serverError'

import type { TransportOfferKindConfig, TransportOfferRow } from '../Contracts/ITransportOffer'

/**
 * Блок без пагінації (`SPEC-024` §7; легасі `OfferCrud.jsx:124`): пропозицій
 * у власника одиниці, з великим запасом — одним запитом. Якщо більше —
 * блок скаже, скільки приховав.
 */
export const TRANSPORT_OFFERS_LIMIT = 100

type CollectionResponse = {
    items?: TransportOfferRow[]
    meta?: { total?: number }
    _permissions?: { create?: boolean }
} | null

/**
 * Пропозиції одного власника для блоку «Цінові пропозиції» (`SPEC-024` §8.1):
 * префільтр `<власник>.uuid` спільної колекції, порядок — як віддає бек
 * (ПР-09). «Додати» — колекційне `_permissions.create` тієї самої відповіді:
 * воно вже враховує, чи читає користувач власника.
 */
export class TransportOffersBlockStore {
    items: TransportOfferRow[] = []
    total = 0
    canCreate = false
    isLoading = false
    failed = false
    removingUuid: string | null = null

    constructor(private readonly config: TransportOfferKindConfig) {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    /**
     * `forceRefresh`: ядровий клієнт дедуплікує GET на 30 с, і без нього
     * щойно додана пропозиція не з'явилася б у блоці. Повертає кількість
     * пропозицій власника або `null`, якщо завантажити не вдалося.
     */
    async load(ownerUuid: string | undefined): Promise<number | null> {
        if (!ownerUuid) return null

        this.isLoading = true
        this.failed = false

        try {
            const filters = JSON.stringify({
                and: [{ field: `${this.config.ownerField}.uuid`, op: '=', value: ownerUuid }],
            })
            const raw = (await restClient.get(
                `${this.config.restUrl}?limit=${TRANSPORT_OFFERS_LIMIT}&permissions=true&filters=${encodeURIComponent(filters)}`,
                undefined,
                { forceRefresh: true }
            )) as unknown as CollectionResponse
            const rows = Array.isArray(raw?.items) ? raw.items.filter((item) => typeof item?.uuid === 'string') : []
            const total = typeof raw?.meta?.total === 'number' ? raw.meta.total : rows.length

            runInAction(() => {
                this.items = rows
                this.total = total
                this.canCreate = raw?._permissions?.create === true
            })

            return total
        } catch {
            runInAction(() => {
                this.items = []
                this.total = 0
                this.canCreate = false
                this.failed = true
            })

            return null
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /**
     * Видаляє пропозицію (жорстко, з файлами — ПР-07). Підтвердження — справа
     * блоку (`Popconfirm`). Повертає текст помилки або `null`.
     */
    async remove(uuid: string): Promise<string | null> {
        if (this.removingUuid !== null) return null

        this.removingUuid = uuid

        try {
            await restClient.delete(`${this.config.restUrl}/${encodeURIComponent(uuid)}`)

            return null
        } catch (error) {
            return serverMessage(error, 'Зверніться до адміністратора системи.')
        } finally {
            runInAction(() => {
                this.removingUuid = null
            })
        }
    }
}
